import { ImageViewer } from "@mirage/components/imageViewer/ImageViewer";
import { DocumentViewer } from "@mirage/components/documentViewer/documentViewer";
import { RemoveTrailingSlash } from "@mirage/components/removeTrailingSlash/RemoveTrailingSlash";
import CustomSnackbar from "@mirage/components/snackbar/CustomSnackbar";
import useSessionStorage from "@mirage/hooks/useSessionStorage";
import { PPConstants } from "@shared/publicportal/constants";
import { Routes, Route, Navigate } from "react-router-dom";
import CookieConsent from "./components/cookieConsent/CookieConsent";
import { ProtectedRoute } from "./components/protectedRoute/ProtectedRoute";
import PublicPortalUserSettings from "./components/publicPortalUserSettings/PublicPortalUserSettings";
import BuildingRegulation from "./pages/buildingRegulation/BuildingRegulation";
import ChangePassword from "./pages/changePassword/ChangePassword";
import CompetentPersonsScheme from "./pages/competentPersonsScheme/CompetentPersonsScheme";
import EnforcementAppeal from "./pages/enforcementAppeal/EnforcementAppeal";
import EnforcementNotice from "./pages/enforcementNotice/EnforcementNotice";
import ForgotPassword from "./pages/forgotPassword/ForgotPassword";
import PublicLogin from "./pages/login/PublicLogin";
import PlanningAppeals from "./pages/planningAppeals/PlanningAppeals";
import PlanningApplication from "./pages/planningApplication/PlanningApplication";
import Register from "./pages/register/PublicRegister";
import TermsAndConditions from "./pages/termsAndConditions/TermsAndConditions";
import ResetPassword from "./pages/resetPassword/ResetPassword";
import BuildingRegulationsSearchResult from "./pages/searchResult/BuildingRegulations/BuildingRegulationsSearchResult";
import EnforcementAppealSearchResult from "./pages/searchResult/EnforcementAppeals/EnforcementAppealsSearchResult";
import EnforcementNoticeSearchResult from "./pages/searchResult/EnforcementNotice/EnforcementNoticeSearchResult";
import { PlanningAppealsSearchResult } from "./pages/searchResult/PlanningAppeals/PlanningAppealsSearchResult";
import PlanningApplicationSearchResult from "./pages/searchResult/PlanningApplication/PlanningApplicationSearchResult";
import PropertySearchResult from "./pages/searchResult/PropertySearch/PropertySearchResult";
import { WeeklyListsSearchResult } from "./pages/searchResult/WeeklyLists/WeeklyListsSearchResult";
import UserCollectedData from "./pages/userCollectedData/UserCollectedData";
import VerifyConsultee from "./pages/verifyConsultee/VerifyConsultee";
import VerifyEmail from "./pages/verifyEmail/VerifyEmail";
import { PublicPortalTheme } from "./theme/MuiPublicPortal.module";
import Search from "./pages/search/Search";
import { PublicPortalRoutingService } from "./services/PublicPortalRoutingService";
import { ThemeProvider } from "@mui/material";
import { IUser } from "@shared/mirage/core/contracts/IUser";
import { UserService } from "./services/UserService";
import UserCustomProperty from "@shared/mirage/utils/getUserCustomProperty";
import ConfirmActionDialog from "@mirage/components/confirmActionDialog/ConfirmActionDialog";

const pp = new PublicPortalRoutingService();

const RouteElement = () => {
    const appName = PPConstants.AppName;
    const [sessionValue] = useSessionStorage(appName);
    const { clientSettings } = sessionValue;
    if (!clientSettings) {
        return null;
    }
    const loginAllowed = clientSettings?.ENABLE_LOGIN;
    const loggedInUser: Partial<IUser> = UserService.getLoggedInUser();
    const consulteeId = UserCustomProperty.getUserCustomPropertyValue(loggedInUser, "consulteeId");

    return (
        <ThemeProvider theme={PublicPortalTheme}>
            <div id="publicPortalApp">
                <RemoveTrailingSlash />
                <Routes>
                    <Route index element={<Search />} />
                    {loginAllowed ? (
                        <>
                            <Route
                                path={`/${pp.Login}/`}
                                element={
                                    <CookieConsent>
                                        <PublicLogin />
                                    </CookieConsent>
                                }
                            />
                            <Route
                                path={`/${pp.ForgotPassword}/`}
                                element={
                                    <CookieConsent>
                                        <ForgotPassword />
                                    </CookieConsent>
                                }
                            />
                            <Route
                                path={`/${pp.ResetPassword}/`}
                                element={
                                    <CookieConsent>
                                        <ResetPassword />
                                    </CookieConsent>
                                }
                            />
                            <Route
                                path={`/${pp.Register}/`}
                                element={
                                    <CookieConsent>
                                        <Register />
                                    </CookieConsent>
                                }
                            />
                            <Route path={`/${pp.VerifyEmail}/`} element={<VerifyEmail />} />
                            <Route path={`/${pp.VerifyConsultee}/`} element={<VerifyConsultee />} />
                        </>
                    ) : null}
                    <Route path={`/${pp.PropertySearch}/`}>
                        <Route path="?filterParams" element={<PropertySearchResult />} />
                        <Route path="" element={<PropertySearchResult />} />
                    </Route>
                    {sessionValue.clientSettings?.COMPETENT_PERSONS_SCHEME ? (
                        <Route path={`/${pp.CompetentPersonsScheme}/`}>
                            <Route path=":id" element={<CompetentPersonsScheme />} />
                            <Route path="" element={<CompetentPersonsScheme />} />
                        </Route>
                    ) : null}
                    {sessionValue.clientSettings?.BUILDING_REGULATIONS ? (
                        <Route path={`/${pp.BuildingRegulations}/`}>
                            <Route path="?filterParams" element={<BuildingRegulationsSearchResult />} />
                            <Route path=":id" element={<BuildingRegulation />} />
                            <Route path="" element={<BuildingRegulationsSearchResult />} />
                        </Route>
                    ) : null}
                    {sessionValue.clientSettings?.PLANNING_APPLICATIONS ? (
                        <Route path={`/${pp.PlanningApplications}/`}>
                            <Route path="?filterParams" element={<PlanningApplicationSearchResult />} />
                            <Route path=":applicationId" element={<PlanningApplication />} />
                            <Route path="" element={<PlanningApplicationSearchResult />} />
                        </Route>
                    ) : null}
                    {sessionValue.clientSettings?.PLANNING_APPEALS ? (
                        <Route path={`/${pp.PlanningAppeals}/`}>
                            <Route path="?filterParams" element={<PlanningAppealsSearchResult />} />
                            <Route path=":id" element={<PlanningAppeals />} />
                            <Route path="" element={<PlanningAppealsSearchResult />} />
                        </Route>
                    ) : null}
                    {sessionValue.clientSettings?.ENFORCEMENT_APPEALS ? (
                        <Route path={`/${pp.EnforcementAppeals}/`}>
                            <Route path="?filterParams" element={<EnforcementAppealSearchResult />} />
                            <Route path=":id" element={<EnforcementAppeal />} />
                            <Route path="" element={<EnforcementAppealSearchResult />} />
                        </Route>
                    ) : null}
                    {sessionValue.clientSettings?.ENFORCEMENT_NOTICES ? (
                        <Route path={`/${pp.EnforcementNotices}/`}>
                            <Route path="?filterParams" element={<EnforcementNoticeSearchResult />} />
                            <Route path=":noticeId" element={<EnforcementNotice />} />
                            <Route path="" element={<EnforcementNoticeSearchResult />} />
                        </Route>
                    ) : null}
                    <Route path={`/${pp.WeeklyLists}/`} element={<WeeklyListsSearchResult />}>
                        <Route path="?filterParams" element={<WeeklyListsSearchResult />} />
                    </Route>
                    {loginAllowed ? (
                        <>
                            <Route path={`/${pp.CollectedData}/`} element={<ProtectedRoute redirectTo={pp.Root} customAllowCondition={!consulteeId} />}>
                                <Route path={`/${pp.CollectedData}/`} element={<UserCollectedData />} />
                            </Route>
                            <Route path={`/${pp.ChangePassword}/`} element={<ProtectedRoute redirectTo={pp.Root} />}>
                                <Route path={`/${pp.ChangePassword}/`} element={<ChangePassword />} />
                            </Route>
                            <Route path={`/${pp.UserSettings}/`} element={<ProtectedRoute redirectTo={pp.Root} />}>
                                <Route path={`/${pp.UserSettings}/`} element={<PublicPortalUserSettings />} />
                            </Route>
                            <Route path={`/${pp.TermsAndConditions}/`} element={<TermsAndConditions />} />
                        </>
                    ) : null}
                    <Route path="*" element={<Navigate to={pp.Root} />} />
                </Routes>
                <CustomSnackbar />
                <ConfirmActionDialog />
                <ImageViewer />
                <DocumentViewer appName="publicportal" />
            </div>
        </ThemeProvider>
    );
};

export default RouteElement;
